import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";

/* ---------- API ---------- */
const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- Helper ---------- */
const fmt = (v) => (v == null ? "0.00" : Number(v).toFixed(2));

/* ---------- Page ---------- */
export default function ViewSales() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });
  const [detailRow, setDetailRow] = React.useState(null);

 const loadSales = React.useCallback(async () => {
  setLoading(true);

  const { data, error } = await getJSON("/api/sales", {
    _: Date.now(),
  });

  setLoading(false);

  if (error) {
    setToast({
      open: true,
      msg: "Failed to load sales",
      sev: "error",
    });
    setRows([]);
    return;
  }

  setRows(Array.isArray(data?.data) ? data.data : []);
}, []);

  React.useEffect(() => {
    loadSales();
  }, [loadSales]);

  const columns = [
  {
    field: "no",
    headerName: "No.",
    width: 80,
    sortable: false,
    valueGetter: (p) =>
      p.api.getRowIndexRelativeToVisibleRows(p.id) + 1,
  },
  {
    field: "salesPerson",
    headerName: "Sales Person",
    flex: 1,
    valueGetter: (p) => p.row.user?.fullName || "—",
  },
  {
    field: "customer",
    headerName: "Customer",
    flex: 1,
    valueGetter: (p) => p.row.customer?.name || "—",
  },
  {
    field: "warehouse",
    headerName: "Warehouse",
    flex: 1,
    valueGetter: (p) => p.row.warehouse?.name || "—",
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
  },
  {
    field: "salesDate",
    headerName: "Sales Date",
    width: 140,
    valueGetter: (p) =>
      p.row.salesDate
        ? p.row.salesDate.slice(0, 10)
        : "—",
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 140,
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Details">
          <IconButton
            size="small"
            onClick={() => setDetailRow(row)}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() =>
              window.location.assign(`/sales/edit/${row.id}`)
            }
          >
            <EditIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteRow(row)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="SALES"
          subtitle="View sales and click details for more info"
        />
     

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Sale</DialogTitle>

        <DialogContent dividers>
          <Typography>Are you sure you want to delete this sale?</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);

              const { error } = await deleteJSON(`/api/sales/${deleteRow.id}`);

              if (error) {
                setToast({
                  open: true,
                  msg: "Failed to delete sale",
                  sev: "error",
                });
                setDeleting(false);
                return;
              }

              setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
              setToast({ open: true, msg: "Sale deleted", sev: "success" });

              setDeleting(false);
              setDeleteRow(null);
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      {detailRow && (
        <Dialog
          open={Boolean(detailRow)}
          onClose={() => setDetailRow(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Sale Details</DialogTitle>
          <DialogContent dividers>
      <Box display="flex" flexDirection="column" gap={1.5}>
  <Typography>
    <b>Sales Person:</b>{" "}
    {detailRow.user?.fullName || "—"}
  </Typography>

  <Typography>
    <b>Customer:</b>{" "}
    {detailRow.customer?.name || "—"}
  </Typography>

  <Typography>
    <b>Warehouse:</b>{" "}
    {detailRow.warehouse?.name || "—"}
  </Typography>

  <Typography>
    <b>Status:</b>{" "}
    {detailRow.status || "—"}
  </Typography>

  <Typography>
    <b>Sales Date:</b>{" "}
    {detailRow.salesDate?.slice(0, 10) || "—"}
  </Typography>

  <Typography>
    <b>Description:</b>{" "}
    {detailRow.description || "—"}
  </Typography>

  {Array.isArray(detailRow.items) &&
    detailRow.items.length > 0 && (
      <>
        <Divider sx={{ my: 2 }} />

        <Typography variant="h6">
          Items
        </Typography>

        {detailRow.items.map((item) => (
          <Box
            key={item.id}
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              p: 2,
              mb: 1,
            }}
          >
            <Typography>
              <b>Name:</b>{" "}
              {item.item?.name || "—"}
            </Typography>

            <Typography>
              <b>Quantity:</b>{" "}
              {Number(item.quantity).toFixed(2)}
            </Typography>

            <Typography>
              <b>Unit Price:</b>{" "}
              {Number(item.unitPrice).toFixed(2)}
            </Typography>

            <Typography>
              <b>Total:</b>{" "}
              {(
                Number(item.quantity) *
                Number(item.unitPrice)
              ).toFixed(2)}
            </Typography>
          </Box>
        ))}
      </>
    )}
</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailRow(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- Subcomponent ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}


